import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { postId } = useParams();
  const navigate = useNavigate();

  // Blog post content
  const blogContent = {
    'how-to-choose-construction-company': {
      title: 'How to Choose the Right Construction Company for Your Home Project',
      date: 'January 7, 2025',
      readTime: '8 min read',
      category: 'Guides',
      heroImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200',
      author: 'T&M Construction Team',
      content: `
        <p class="lead">Choosing the right construction company is one of the most important decisions you'll make for your home improvement project. Whether you're planning a kitchen renovation, bathroom installation, or a complete property refurbishment, the company you choose will directly impact the quality, timeline, and overall success of your project.</p>

        <p>In this comprehensive guide, we'll walk you through the essential factors to consider, helping you make an informed decision that you'll be happy with for years to come.</p>

        <h2>1. Check Credentials and Registration</h2>
        
        <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800" alt="Professional construction credentials" class="rounded-xl my-6" />
        
        <p>The first step in choosing a construction company is verifying their credentials. A legitimate company should be:</p>
        
        <ul>
          <li><strong>Registered with Companies House</strong> - This proves they're a legitimate business entity</li>
          <li><strong>VAT Registered</strong> - For larger companies, this indicates they handle significant turnover</li>
          <li><strong>Properly Insured</strong> - Look for Public Liability Insurance and Employer's Liability Insurance</li>
        </ul>

        <h2>2. Review Their Portfolio and Past Work</h2>
        
        <img src="https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800" alt="Kitchen renovation project" class="rounded-xl my-6" />
        
        <p>A reputable construction company should have a portfolio of completed projects. When reviewing their work, look for projects similar to yours in scope and style, and ask for before and after photos.</p>

        <h2>3. Get Multiple Quotes</h2>
        
        <p>Never settle for the first quote you receive. Getting multiple quotes helps you understand the market rate, compare approaches, and identify red flags.</p>

        <h2>4. Communication is Key</h2>
        
        <p>A good construction company should be easy to communicate with from the very first interaction. Consider how quickly they respond to enquiries and whether they're willing to answer all your questions.</p>

        <h2>5. Understand the Contract</h2>
        
        <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800" alt="Construction contract" class="rounded-xl my-6" />
        
        <p>Before any work begins, ensure you have a clear, written contract that includes detailed scope of work, materials to be used, total cost and payment schedule, and warranty information.</p>

        <h2>Conclusion</h2>
        
        <p>Choosing the right construction company requires research, patience, and careful consideration. By following these guidelines, you'll be well-equipped to find a company that delivers quality work and makes your renovation dreams a reality.</p>
      `
    },
    'winter-construction-tips': {
      title: 'Essential Winter Construction Tips: Protecting Your Project in Cold Weather',
      date: 'January 6, 2025',
      readTime: '7 min read',
      category: 'Seasonal',
      heroImage: 'https://images.unsplash.com/photo-1516557070061-c3d1653fa646?w=1200',
      author: 'T&M Construction Team',
      content: `
        <p class="lead">Winter construction presents unique challenges, but with proper planning and precautions, your project can proceed successfully even in cold weather. Here's what you need to know.</p>

        <h2>Understanding Winter Construction Challenges</h2>
        
        <img src="https://images.unsplash.com/photo-1491002052546-bf38f186af56?w=800" alt="Winter construction site" class="rounded-xl my-6" />
        
        <p>Cold weather affects construction in several ways:</p>
        
        <ul>
          <li><strong>Concrete curing</strong> - Below 5°C, concrete takes longer to cure and can be damaged by frost</li>
          <li><strong>Paint and adhesives</strong> - Many products won't set properly in cold temperatures</li>
          <li><strong>Shorter daylight hours</strong> - Less working time means projects may take longer</li>
          <li><strong>Ground conditions</strong> - Frozen or waterlogged ground can delay excavation work</li>
        </ul>

        <h2>Best Projects for Winter</h2>
        
        <p>Some projects are actually ideal for winter:</p>
        
        <ul>
          <li><strong>Interior renovations</strong> - Kitchens, bathrooms, and internal remodelling</li>
          <li><strong>Plastering and painting</strong> - Indoor work in heated spaces</li>
          <li><strong>Electrical and plumbing</strong> - Most internal work is unaffected by weather</li>
          <li><strong>Planning and design</strong> - Perfect time to prepare for spring construction</li>
        </ul>

        <h2>Protecting Materials and Workers</h2>
        
        <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800" alt="Construction workers" class="rounded-xl my-6" />
        
        <p>Proper storage of materials is crucial. Keep cement, plaster, and other moisture-sensitive materials in dry, covered areas. Ensure workers have appropriate cold-weather gear and take regular breaks to warm up.</p>

        <h2>Cost Considerations</h2>
        
        <p>Winter construction can sometimes be more cost-effective as contractors may have more availability. However, factor in potential delays and additional heating costs for indoor work.</p>

        <h2>Planning Ahead</h2>
        
        <p>The best approach is to plan winter work in advance. Schedule interior work for colder months and save exterior projects for spring and summer when conditions are optimal.</p>
      `
    },
    'spring-renovation-guide': {
      title: 'Spring Renovation Guide: The Perfect Time to Transform Your Home',
      date: 'January 5, 2025',
      readTime: '6 min read',
      category: 'Seasonal',
      heroImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200',
      author: 'T&M Construction Team',
      content: `
        <p class="lead">Spring is widely considered the best season for home renovations. With milder temperatures and longer days, it's the perfect time to tackle those projects you've been planning all winter.</p>

        <h2>Why Spring is Ideal for Renovations</h2>
        
        <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800" alt="Spring home renovation" class="rounded-xl my-6" />
        
        <ul>
          <li><strong>Optimal weather conditions</strong> - Not too hot, not too cold</li>
          <li><strong>Perfect for exterior work</strong> - Painting, roofing, and landscaping</li>
          <li><strong>Windows can stay open</strong> - Better ventilation for painting and varnishing</li>
          <li><strong>Ground conditions improve</strong> - Easier for foundation and garden work</li>
        </ul>

        <h2>Best Spring Projects</h2>
        
        <h3>Exterior Painting</h3>
        <p>Spring temperatures (10-25°C) are ideal for exterior paint application. The paint dries properly without the risk of frost damage or heat blistering.</p>

        <h3>Landscaping and Driveways</h3>
        <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800" alt="Garden landscaping" class="rounded-xl my-6" />
        <p>The ground has thawed but isn't yet baked hard by summer sun, making it easier to work with for patios, decking, and garden projects.</p>

        <h3>Roof Repairs</h3>
        <p>After winter storms, spring is the time to assess and repair any roof damage before the next winter arrives.</p>

        <h2>Planning Your Spring Project</h2>
        
        <p>Book your contractor early! Spring is peak season, and the best companies get booked up quickly. Start planning in January or February for spring work.</p>

        <h2>Preparing Your Home</h2>
        
        <p>Before work begins, clear the areas that will be affected. Protect furniture and belongings, and ensure easy access for workers and materials.</p>
      `
    },
    'summer-building-projects': {
      title: 'Summer Building Projects: Making the Most of Long Days',
      date: 'January 4, 2025',
      readTime: '6 min read',
      category: 'Seasonal',
      heroImage: 'https://images.unsplash.com/photo-1513880989635-6eb491ce7f5b?w=1200',
      author: 'T&M Construction Team',
      content: `
        <p class="lead">Summer offers the longest working days and most reliable weather, making it ideal for major construction projects. Here's how to make the most of the season.</p>

        <h2>Advantages of Summer Construction</h2>
        
        <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800" alt="Summer construction" class="rounded-xl my-6" />
        
        <ul>
          <li><strong>Extended daylight</strong> - Up to 16 hours of daylight for work</li>
          <li><strong>Reliable weather</strong> - Less rain delays (usually!)</li>
          <li><strong>Faster concrete curing</strong> - Warm temperatures speed up the process</li>
          <li><strong>Ideal for major structural work</strong> - Extensions, loft conversions</li>
        </ul>

        <h2>Best Summer Projects</h2>
        
        <h3>House Extensions</h3>
        <p>Major structural work requiring open walls is best done in summer when rain is less likely and drying times are faster.</p>

        <h3>Loft Conversions</h3>
        <img src="https://images.unsplash.com/photo-1513880989635-6eb491ce7f5b?w=800" alt="Loft conversion" class="rounded-xl my-6" />
        <p>Opening up the roof is best done in dry, warm weather. Summer allows for faster completion with less risk of water damage.</p>

        <h3>Outdoor Living Spaces</h3>
        <p>Patios, decking, pergolas, and outdoor kitchens - all perfect summer projects that you can enjoy as soon as they're finished!</p>

        <h2>Managing Summer Challenges</h2>
        
        <h3>Heat Management</h3>
        <p>Very hot weather can affect paint application and worker comfort. Schedule early morning starts during heatwaves.</p>

        <h3>Holiday Season</h3>
        <p>Summer holidays can affect material deliveries and subcontractor availability. Plan around school holidays if possible.</p>

        <h2>Booking Summer Work</h2>
        
        <p>Summer is the busiest season for construction. Book your project 2-3 months in advance to secure your preferred dates and avoid disappointment.</p>
      `
    },
    'kitchen-renovation-trends-2025': {
      title: 'Top Kitchen Renovation Trends for 2025',
      date: 'January 3, 2025',
      readTime: '6 min read',
      category: 'Trends',
      heroImage: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200',
      author: 'T&M Construction Team',
      content: `
        <p class="lead">The kitchen remains the heart of the home, and 2025 brings exciting new trends that blend functionality with stunning aesthetics. Here's what's shaping kitchen design this year.</p>

        <h2>1. Smart Kitchen Integration</h2>
        <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800" alt="Modern smart kitchen" class="rounded-xl my-6" />
        <p>Smart technology is no longer optional—it's expected. From voice-controlled appliances to app-connected ovens, the modern kitchen is becoming increasingly intelligent.</p>

        <h2>2. Sustainable Materials</h2>
        <p>Eco-conscious homeowners are choosing recycled materials, bamboo cabinetry, and energy-efficient appliances to reduce their carbon footprint.</p>

        <h2>3. Bold Colour Choices</h2>
        <img src="https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=800" alt="Bold kitchen colours" class="rounded-xl my-6" />
        <p>While white kitchens remain timeless, 2025 sees a surge in bold colours—deep greens, navy blues, and even black cabinetry are making statements.</p>

        <h2>4. Open Shelving</h2>
        <p>Moving away from closed cabinets, open shelving creates a more spacious feel and allows for decorative displays.</p>

        <h2>5. Large Format Tiles</h2>
        <p>Bigger tiles mean fewer grout lines and a cleaner, more modern appearance.</p>
      `
    },
    'bathroom-transformation-guide': {
      title: 'The Complete Guide to Bathroom Transformations',
      date: 'January 2, 2025',
      readTime: '7 min read',
      category: 'Guides',
      heroImage: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200',
      author: 'T&M Construction Team',
      content: `
        <p class="lead">A bathroom renovation can transform your daily routine and add significant value to your property. Here's everything you need to know about planning your bathroom transformation.</p>

        <h2>Planning Your Bathroom Renovation</h2>
        <img src="https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800" alt="Modern bathroom design" class="rounded-xl my-6" />
        <p>Before starting any work, careful planning is essential. Consider your budget, timeline, and must-have features.</p>

        <h2>Choosing the Right Fixtures</h2>
        <p>From rainfall showers to freestanding baths, the fixtures you choose will define the character of your bathroom. Quality matters—invest in durable products that will stand the test of time.</p>

        <h2>Maximising Small Spaces</h2>
        <p>Even small bathrooms can feel luxurious with the right design choices. Wall-hung toilets, corner sinks, and clever storage solutions make a big difference.</p>

        <h2>Lighting Considerations</h2>
        <p>Good bathroom lighting combines task lighting for grooming with ambient lighting for relaxation. Consider dimmable options for versatility.</p>

        <h2>Working with Professionals</h2>
        <p>A bathroom renovation involves plumbing, electrical work, tiling, and more. Working with experienced professionals ensures everything is done safely and to code.</p>
      `
    }
  };

  const post = blogContent[postId];
  const [apiPost, setApiPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Reset state when postId changes
    setApiPost(null);
    setNotFound(false);
    
    if (post) {
      // Hardcoded post found, no need to fetch
      setLoading(false);
    } else {
      // Fetch from API
      setLoading(true);
      axios.get(`${API}/api/blog/${postId}`)
        .then(res => {
          setApiPost(res.data);
          setLoading(false);
        })
        .catch(() => {
          setNotFound(true);
          setLoading(false);
        });
    }
  }, [postId, post]);

  const displayPost = post || (apiPost ? {
    ...apiPost,
    heroImage: apiPost.image || apiPost.heroImage || '',
    date: apiPost.created_at ? new Date(apiPost.created_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
  } : null);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-56">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!displayPost || notFound) {
    return (
      <div className="min-h-screen bg-white pt-56">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-orange-600 hover:bg-orange-700">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={displayPost?.title || 'Blog Post'}
        description={displayPost?.content ? displayPost.content.replace(/<[^>]*>/g, '').substring(0, 160) : ''}
        canonical={`/blog/${postId}`}
      />
      
      {/* Hero Image */}
      <div className="relative h-96 md:h-[500px]">
        <img 
          src={displayPost?.heroImage || ''} 
          alt={displayPost?.title || 'Blog Post'}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="container mx-auto">
            <div className="max-w-4xl">
              <button 
                onClick={() => navigate(-1)} 
                className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
              >
                <LucideIcons.ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </button>
              <span className="block text-orange-400 font-medium mb-2">{displayPost?.category || ''}</span>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">{displayPost?.title || ''}</h1>
              <div className="flex items-center space-x-4 text-gray-300">
                <span>{displayPost?.date || ''}</span>
                <span>•</span>
                <span>{displayPost?.readTime || ''}</span>
                <span>•</span>
                <span>By {displayPost?.author || 'T&M Construction Team'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div 
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-orange-600"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayPost?.content || '') }}
            />

            {/* Author & Share */}
            <div className="border-t border-gray-200 mt-12 pt-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                    <LucideIcons.HardHat className="w-8 h-8 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{displayPost?.author || 'T&M Construction Team'}</p>
                    <p className="text-gray-500 text-sm">Professional builders serving Suffolk, Essex & London</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-gray-500 text-sm">Share this article:</span>
                  <div className="flex space-x-2">
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                      <LucideIcons.Facebook className="w-5 h-5" />
                    </a>
                    <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(displayPost?.title || '')}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors">
                      <LucideIcons.Twitter className="w-5 h-5" />
                    </a>
                    <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(displayPost?.title || '')}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition-colors">
                      <LucideIcons.Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Back Navigation */}
            <div className="mt-8 flex justify-between items-center">
              <button 
                onClick={() => navigate(-1)} 
                className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
              >
                <LucideIcons.ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </button>
              <Link to="/blog" className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium">
                View All Articles
                <LucideIcons.ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote from our experienced team
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-8">
                Get Your Free Quote
              </Button>
            </Link>
            <Link to="/projects">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                View Our Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
